import { forwardRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import ContainerVirtualizedList from './containerVirtualizedList';
import ExternalElementVirtualizedList from './externalElementVirtualizedList';
import WindowVirtualizedList from './windowVirtualizedList';
import { scrollElementRefPropType, virtualizerRefPropType } from './propTypes';

function VirtualizedListComponent(
  {
    items,
    count = 0,
    gap = 0,
    estimateSize = undefined,
    isDynamicSize = false,
    shouldUseWindowScroll = false,
    scrollElementRef = undefined,
    className = undefined,
    overlayWrapperClass = undefined,
    viewportClass = undefined,
    itemClass = undefined,
    verticalScrollClass = undefined,
    horizontalScrollClass = undefined,
    virtualizerRef = undefined,
    hasNextPage = false,
    isNextPageLoading = false,
    renderItem = item => item,
    onLoadMore = () => {},
    ...rest
  },
  ref
) {
  const getItemKey = useCallback(
    index => {
      const item = items[index];

      return item?.id ?? index;
    },
    [items]
  );

  const resolvedEstimateSize = useCallback(
    index => {
      if (estimateSize) {
        return estimateSize(index);
      }

      return isDynamicSize ? 50 : 0;
    },
    [estimateSize, isDynamicSize]
  );

  const sharedProps = {
    ...rest,
    ref,
    items,
    count,
    gap,
    isDynamicSize,
    estimateSize: resolvedEstimateSize,
    getItemKey,
    scrollElementRef,
    className,
    overlayWrapperClass,
    viewportClass,
    itemClass,
    verticalScrollClass,
    horizontalScrollClass,
    virtualizerRef,
    hasNextPage,
    isNextPageLoading,
    renderItem,
    onLoadMore,
  };

  if (shouldUseWindowScroll && scrollElementRef) {
    return <ExternalElementVirtualizedList {...sharedProps} />;
  }

  if (shouldUseWindowScroll) {
    return <WindowVirtualizedList {...sharedProps} />;
  }

  return <ContainerVirtualizedList {...sharedProps} />;
}

const VirtualizedList = forwardRef(VirtualizedListComponent);

VirtualizedList.displayName = 'VirtualizedList';

VirtualizedList.propTypes = {
  className: PropTypes.string,
  overlayWrapperClass: PropTypes.string,
  viewportClass: PropTypes.string,
  itemClass: PropTypes.string,
  verticalScrollClass: PropTypes.string,
  horizontalScrollClass: PropTypes.string,
  virtualizerRef: virtualizerRefPropType,
  scrollElementRef: scrollElementRefPropType,
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  count: PropTypes.number,
  gap: PropTypes.number,
  estimateSize: PropTypes.func,
  isDynamicSize: PropTypes.bool,
  shouldUseWindowScroll: PropTypes.bool,
  hasNextPage: PropTypes.bool,
  isNextPageLoading: PropTypes.bool,
  renderItem: PropTypes.func,
  onLoadMore: PropTypes.func,
};

export default VirtualizedList;
